import { nanoid } from 'nanoid'
import { Buffer } from 'buffer'

export interface TrackingIDs {
  mids: Array<string>
  tids: Array<string>
}

export interface Message {
  id: string
  ids: TrackingIDs
  version: string
  data: any
  timestamp: number
}

export async function createHash(data: Object | string): Promise<string> {
  const stringValue = typeof data === 'string' ? data : JSON.stringify(data)
  const hashBuffer = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(stringValue))
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

// export function md5(data: Object | string): string {
//   const stringValue = typeof data === 'string' ? data : JSON.stringify(data)
//   return crypto.createHash('md5').update(stringValue).digest('hex')
// }

export function checkEmail(v: string) {
  return /[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(v)
}

export function checkName(v: string) {
  return /^[a-zA-Z]{2,}$/.test(v)
}

export function normalizeLowercaseInput(value: string) {
  return value.toLowerCase()
}

export function sanitizeString(input: string) {
  return input.replace(/[^a-zA-Z0-9_\-\.]/g, '_')
}

export function modelNameToId(input: string) {
  return input.replace(/[^a-zA-Z0-9_\-\.]/g, '-')
}

export function newId(length: number = 21) {
  return nanoid(length)
}

export async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function base64ToObject<T>(base64: string): T {
  const jsonString = Buffer.from(base64, 'base64').toString('utf8')
  return JSON.parse(jsonString) as T
}

export function decodeBase64(encodedString: string): string {
  try {
    return Buffer.from(encodedString, 'base64').toString('utf8');
  } catch (error) {
    console.error('Failed to decode base64 string:', error);
    return encodedString; // Return original string if decoding fails
  }
}

export function camelToSnake(camelCase: string): string {
  return camelCase.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

export function retryWithExponentialBackoff(
  fn: (...args: any) => Promise<any>,
  onRetry: () => any = () => null,
  maxAttempts = 5,
  baseDelayMs = 500
) {
  let attempt = 1

  const execute = async (): Promise<any> => {
    try {
      return await fn()
    } catch (error) {
      if (attempt >= maxAttempts) {
        // throw error
        console.error(error)
        return { error }
      }

      const delayMs = baseDelayMs * 2 ** attempt
      console.log(`Retry attempt ${attempt} after ${delayMs}ms`)
      try {
        onRetry()
      } catch (error) {
        console.log('could not execute onRetry')
      }
      await sleep(delayMs)

      attempt++
      return execute()
    }
  }

  return execute()
}

export interface TextAndThinking {
  thinking: string
  text: string
}

export function extractThinkAndText(input: string): TextAndThinking {
  if (!input.includes('<think>')) {
    return { thinking: '', text: input }
  }
  // Match content between <think> and </think> (non-greedy, across newlines)
  const regex = /<think>([\s\S]*?)<\/think>/
  const match = regex.exec(input)

  let thinking = ''
  let text = input

  if (match) {
    thinking = match[1]
    // Remove the matched <think>...</think> portion from the input
    text = input.replace(match[0], '')
  }

  return { thinking, text }
}

export interface ResponseChunk {
  text: string
  code: boolean
  language?: string
}

export function splitTextIntoChunks(text: string, pattern: { start: string; stop: string }): ResponseChunk[] {
  // Construct a regex that matches code blocks with an optional language specifier
  const modifiedStart = `${pattern.start}(\\w*)\\s*`
  const regex = new RegExp(`${modifiedStart}((?:.|\\n)*?)${pattern.stop}`, 'gs')

  let lastIndex = 0
  let result = []

  // Extract all code blocks and intersperse them with non-code text
  text.replace(regex, (match, language, capturedContent, offset) => {
    // Capture text before the code block
    if (offset > lastIndex) {
      result.push({ text: text.substring(lastIndex, offset), code: false })
    }
    // Capture the code block, excluding the start and stop patterns, and include the language
    language = language.trim()
    if (language === 'json' || language === 'markdown') {
      language = 'javascript'
    }
    result.push({ text: capturedContent.trim(), code: true, language: language.trim() || undefined })
    lastIndex = offset + match.length
    return match // Necessary to keep replace function working
  })

  // Capture any remaining text after the last code block
  if (lastIndex < text.length) {
    result.push({ text: text.substring(lastIndex), code: false })
  }

  return result
}

export function parseText(text: string): ResponseChunk[] {
  const patterns = [
    { start: '```', stop: '```' },
    { start: '---START CODE---', stop: '---END CODE---' }
  ]
  let parsed: { code: boolean; text: string; language?: string }[] = []
  for (const p of patterns) {
    const { start, stop } = p
    parsed = splitTextIntoChunks(text, { start, stop })
    if (parsed.length > 1 || (parsed.length === 1 && parsed[0].code)) {
      break
    }
  }

  return parsed
}

export interface ThinkResponse {
  thinking: string
  chunks: ResponseChunk[]
}

export function parseThinkingResponse(response: string): ThinkResponse {
  const { thinking, text } = extractThinkAndText(response)
  const chunks = parseText(text)
  return { thinking, chunks }
}

/**
 * Browser-safe bytes/base64 helpers (used by PublicAgentChat audio pipeline)
 */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

export function base64ToBytes(b64: string): Uint8Array {
  let s = b64.replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/')
  const pad = s.length % 4
  if (pad === 2) s += '=='
  else if (pad === 3) s += '='
  else if (pad === 1) {
    s = s.slice(0, -1)
    const p2 = s.length % 4
    if (p2 === 2) s += '=='
    else if (p2 === 3) s += '='
  }
  const bin = atob(s)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, c) => sum + c.byteLength, 0)
  const out = new Uint8Array(total)
  let offset = 0
  for (const c of chunks) { out.set(c, offset); offset += c.byteLength }
  return out
}
